import {
  primaryFontFamily,
  styled,
  styledTheme,
} from '@openeducation/oe-it-themes';
import {
  PrismicRichText as RichText,
  PrismicRichTextProps as RichTextProps,
} from '@prismicio/react';
import { RichTextNodeType, RTAnyNode } from '@prismicio/types';
import React from 'react';
import { StyleProp, Text, TextStyle, View } from 'react-native';

import { flex, fs, color, mb, pl, pv } from '../../style-utils';

// NOTE: styled components don't work with prismic, that's why we can't use OEText component here

interface ThemedTextProps {
  style?: StyleProp<TextStyle>;
  children?: React.ReactNode;
}

const ThemedText: React.FC<ThemedTextProps> = ({ style, children }) => {
  return (
    <Text style={[color(styledTheme.color.dark1), style]}>{children}</Text>
  );
};

const propsWithUniqueKey = function (props: object, key: string) {
  return Object.assign(props || {}, { key });
};

export const htmlSerializer = function (
  type:
    | 'em'
    | 'embed'
    | 'label'
    | 'span'
    | 'strong'
    | 'image'
    | 'heading1'
    | 'heading2'
    | 'heading3'
    | 'heading4'
    | 'heading5'
    | 'heading6'
    | 'paragraph'
    | 'preformatted'
    | 'hyperlink'
    | 'list-item'
    | 'o-list-item'
    | 'group-list-item'
    | 'group-o-list-item',
  _node: RTAnyNode,
  text: string | undefined,
  children: JSX.Element[],
  key: string,
  fontSize?: number
) {
  const props = {};

  switch (type) {
    case RichTextNodeType.heading1:
      return (
        <ThemedText
          style={[fs(fontSize ? fontSize : 28), mb(15)]}
          {...propsWithUniqueKey(props, key)}
        >
          {children}
        </ThemedText>
      );
    case RichTextNodeType.heading2:
      return (
        <ThemedText
          style={[fs(fontSize ? fontSize : 20), mb(10)]}
          {...propsWithUniqueKey(props, key)}
        >
          {children}
        </ThemedText>
      );
    case RichTextNodeType.heading3:
      return (
        <ThemedText
          style={[fs(fontSize ? fontSize : 18)]}
          {...propsWithUniqueKey(props, key)}
        >
          {children}
        </ThemedText>
      );
    case RichTextNodeType.heading4:
      return (
        <ThemedText
          style={[fs(fontSize ? fontSize : 16)]}
          {...propsWithUniqueKey(props, key)}
        >
          {children}
        </ThemedText>
      );
    case RichTextNodeType.heading5:
      return (
        <ThemedText
          style={[fs(fontSize ? fontSize : 14)]}
          {...propsWithUniqueKey(props, key)}
        >
          {children}
        </ThemedText>
      );
    case RichTextNodeType.heading6:
      return (
        <ThemedText
          style={[fs(fontSize ? fontSize : 12)]}
          {...propsWithUniqueKey(props, key)}
        >
          {children}
        </ThemedText>
      );
    case RichTextNodeType.paragraph:
      return (
        <ThemedText
          style={[pv(10), fs(fontSize ? fontSize : 12)]}
          {...propsWithUniqueKey(props, key)}
        >
          {children}
        </ThemedText>
      );
    case RichTextNodeType.em:
      return (
        <ThemedText
          style={primaryFontFamily['italic']}
          {...propsWithUniqueKey(props, key)}
        >
          {children}
        </ThemedText>
      );
    case RichTextNodeType.listItem:
    case RichTextNodeType.oListItem:
      return (
        <View
          style={[pv(4), flex('flex-row')]}
          {...propsWithUniqueKey(props, key)}
        >
          <ListItemMark />
          <ThemedText
            style={[
              flex('flex-1'),
              fs(fontSize ? fontSize : 12),
              primaryFontFamily['regular'],
            ]}
          >
            {children}
          </ThemedText>
        </View>
      );
    case RichTextNodeType.list:
    case RichTextNodeType.oList:
      return (
        <View style={[pl(15)]} {...propsWithUniqueKey(props, key)}>
          {children}
        </View>
      );
    case RichTextNodeType.strong:
      return (
        <ThemedText
          style={primaryFontFamily['bold']}
          {...propsWithUniqueKey(props, key)}
        >
          {children}
        </ThemedText>
      );
    case RichTextNodeType.span:
      if (!text) {
        return null;
      }

      return (
        <ThemedText {...propsWithUniqueKey(props, key)}>{text}</ThemedText>
      );
    default:
      return null;
  }
};

export interface PrismicRichTextProps extends Partial<RichTextProps> {
  fontSize?: number;
}

export default function PrismicRichText({
  field,
  fontSize,
}: PrismicRichTextProps) {
  return (
    <RichText
      field={field}
      components={(...args) => htmlSerializer(...args, fontSize)}
    />
  );
}

const LI_MARK_SIZE = 5;

const ListItemMark = styled.View`
  background-color: ${({ theme }) => theme.color.dark1};
  margin-right: 10px;
  margin-top: 7px;
  width: ${LI_MARK_SIZE}px;
  height: ${LI_MARK_SIZE}px;
  border-radius: ${LI_MARK_SIZE / 2}px;
`;
